package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.UiCanvasData
import top.wcpe.allincore.dsl.ir.model.UiModifier
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.layout.engine.PositionMode
import top.wcpe.allincore.ui.runtime.animation.AnimationScheduler
import top.wcpe.allincore.ui.runtime.element.CanvasElement
import top.wcpe.allincore.ui.runtime.element.CarouselView
import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.DropdownOption
import top.wcpe.allincore.ui.runtime.element.ModelPreview
import top.wcpe.allincore.ui.runtime.element.ModelPreviewInstanceKeys
import top.wcpe.allincore.ui.runtime.element.MultiColumnVirtualList
import top.wcpe.allincore.ui.runtime.element.PageView
import top.wcpe.allincore.ui.runtime.element.RadioGroup
import top.wcpe.allincore.ui.runtime.element.ScrollDecorator
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.Toast
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.VirtualGrid
import top.wcpe.allincore.ui.runtime.element.VirtualList
import top.wcpe.allincore.ui.runtime.element.VirtualMasonry
import top.wcpe.allincore.ui.runtime.element.VirtualTree
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.concurrent.ConcurrentMutableMap
import top.wcpe.allincore.ui.runtime.internal.debug.uiDebugEnabled
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.call.BoundFunRefValue
import top.wcpe.allincore.ui.runtime.ir.call.CallDispatcher
import top.wcpe.allincore.ui.runtime.ir.call.FunRefValue
import top.wcpe.allincore.ui.runtime.ir.call.LambdaValue
import top.wcpe.allincore.ui.runtime.ir.call.LocalFunValue
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.ir.prop.PropAppliers
import top.wcpe.allincore.ui.runtime.lifecycle.LifecycleUtils
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import top.wcpe.allincore.ui.runtime.reactive.Signal
import top.wcpe.allincore.ui.runtime.style.VisualModifier
import top.wcpe.allincore.ui.runtime.theme.ThemeEnvironment

/**
 * 递归建树核心协作者：把单个 [UiNode] 分派、实例化为 [UIElement]，并应用属性 / 安装事件。
 *
 * 它是循环互递归的「中心」：[buildElement] 对 conditional 节点调
 * [ConditionalRecomposer.buildConditionalContainer]、对数据驱动 diff 调 [DataDrivenDiffer]
 * 的 diff/template/虚拟项方法时，**把自身 `::buildElement` 作为注入函数 `buildChild` 传给它们**——
 * 由此两个协作者无需静态引用本类即可回调建树，循环依赖在编译期被打破。
 *
 * [buildElement] 设为 internal：供 [IrSessionAssembler] 构建根元素调用，亦供自身 `::buildElement` 引用。
 */
@Suppress("TooManyFunctions", "LargeClass", "MagicNumber")
internal object ElementTreeBuilder {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.IrInterpreter")
    private const val MODEL_PREVIEW_BASE_PROP = "__modelPreviewBase"

    /**
     * 属性绑定失败日志限流：同一 (screenId, elementId, key) 只在首次失败时打印完整日志，
     * 后续相同失败静默吞掉，避免每帧重复刷屏。
     */
    private val BINDING_FAILURE_LOGGED = ConcurrentMutableMap<String, Boolean>()

    /** 透传到每个生成 slot 的共享 prop 名单（与 InventoryExpanderPass / SlotGridExpanderPass 对齐）。 */
    private val SHARED_SLOT_PROPS: List<String> = listOf(
        "source", "key", "readOnly", "allowShiftTransfer", "allowDrop", "allowPick",
        "slotType", "predict", "placeholder",
    )

    /** 透传到每个生成 slot 的共享视觉 modifier 字段。 */
    private val SHARED_SLOT_MODIFIER_FIELDS: Set<String> = setOf("borderColor", "borderWidth")

    /** box 包装容器保留的几何 / 定位类 prop 名单。 */
    private val BOX_KEEP_PROPS: List<String> = listOf("x", "y", "positionMode")

    private fun logBindingFailureOnce(screenId: String, el: UIElement<*>, key: String, message: String?) {
        val elementId = if (el.id() != null) el.id() else el::class.simpleName
        val logKey = "$screenId|$elementId|$key"
        // 表达式绑定求值失败属高频降级（已有 BINDING_FAILURE_LOGGED 静默机制），详细诊断时再打
        if (uiDebugEnabled() &&
            BINDING_FAILURE_LOGGED.putIfAbsent(logKey, true) == null
        ) {
            LOGGER.debug("[ui][$screenId] 元素 '$elementId' 属性 $key 的表达式绑定执行失败（后续相同错误将静默）：$message")
        }
    }

    fun buildElement(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val element = buildElementInternal(node, ctx)
        element.assignRuntimeNodePath(ModelPreviewInstanceKeys.encode(ctx.runtimeNodePath, ctx.runtimeDynamicKey))
        return element
    }

    @Suppress(
        "CyclomaticComplexMethod",
        "LongMethod",
        "NestedBlockDepth",
        "ReturnCount",
        "CognitiveComplexMethod",
        "ComplexCondition",
    )
    private fun buildElementInternal(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val kind = node.kind
        // RFC-RECOMPOSITION：conditional 节点优先于其他分派。kind="$cond"。
        if (node.isConditional) {
            return ConditionalRecomposer.buildConditionalContainer(node, ctx, ::buildElement)
        }
        // $for：for 循环重组节点。kind="$for"，children 是单根循环体模板。
        if (node.isFor) {
            return buildForContainer(node, ctx)
        }
        // B4：dynamic inventory / slotGrid 运行时就地展开为 box+slots（一次构造）
        if ("inventory" == kind && isDynamic(node)) {
            return buildDynamicInventory(node, ctx)
        }
        if ("slotGrid" == kind && isDynamic(node)) {
            return buildDynamicSlotGrid(node, ctx)
        }
        // 滚动家族窗口化容器：按 kind 优先分派（VirtualTree 用 source 驱动 roots，
        // 故须在通用 hasSource 分支之前拦截，避免被 buildDataDrivenContainer 抢走）。
        if ("virtualList" == kind) {
            return buildVirtualList(node, ctx)
        }
        if ("virtualGrid" == kind) {
            return buildVirtualGrid(node, ctx)
        }
        if ("virtualMasonry" == kind) {
            return buildVirtualMasonry(node, ctx)
        }
        if ("virtualTree" == kind) {
            return buildVirtualTree(node, ctx)
        }
        if ("multiColumnList" == kind) {
            return buildMultiColumnList(node, ctx)
        }
        // PageView / Carousel：分页容器，children 即页，eager 实例化并强制占满视窗
        if ("pageview" == kind || "carousel" == kind) {
            return buildPageView(node, ctx)
        }
        // Phase 3 C 类装饰器：包裹型容器,子节点正常实例化后把首个滚动容器子设为 body。
        if ("pullToRefresh" == kind ||
            "infiniteScroll" == kind ||
            "zoomable" == kind ||
            "autoScroll" == kind ||
            "dragReorder" == kind
        ) {
            return buildScrollDecorator(node, ctx)
        }
        // M6-c：数据驱动容器（source = ...）走独立分支，children 是模板，不直接实例化
        if (node.hasSource()) {
            return buildDataDrivenContainer(node, ctx)
        }
        // 兜底：万一手构 IR 里出现 dynamic=false 的 inventory / slotGrid（编译期理应已展开），
        // IrElementFactory 会 warn + 回退 Box，子节点照常递归。
        // canvas 走 create(node) 重载携带 CanvasData；其余 kind 等价。
        val el: UIElement<*> = IrElementFactory.create(node)
        el.id(node.id)
        if (el is ModelPreview) {
            val baseBinding = node.props[MODEL_PREVIEW_BASE_PROP]
            val modelBase = if (baseBinding?.type == UiBinding.BindingType.LITERAL) {
                baseBinding.literalValue as? String ?: ""
            } else {
                ""
            }
            el.runtimeMetadata(modelBase, ctx.runtimeNodePath, ctx.runtimeDynamicKey)
        }
        // 计划 07 M5：把 IR 元素节点的 animateLayout 标记位接到 runtime 字段，
        // 让 LayoutAnimator 据此 opt-in 捕获该元素的布局位置变化并补间（FLIP）。
        if (node.isAnimateLayout) {
            el.animateLayout(true)
        }
        // M11.7 Stage 1：canvas 元素的 width/height/duration 若为表达式，
        // 注册 Effect 把求值结果回写到 CanvasElement.updateDimensions。
        // 跨模块无法对公开属性 canvasData smart-cast，取局部 val 固定后判空。
        val canvasData = node.canvasData
        if (el is CanvasElement && canvasData != null) {
            // M11.7 hotfix：注入当前 EvalContext，让 draw 块内 state.x 引用可解析。
            el.evalContext(ctx.evalCtx)
            bindCanvasDimensions(el, canvasData, ctx)
        }
        // M6-i：动画引用必须在 applyProp 之前装配，否则形如 `clickComplete` 的 prop
        // 在装配 onComplete 回调时无法识别其对应的触发器。
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) {
                el.animationRef(e.key, animation)
            }
        }
        applyModifiers(el, node, ctx)
        for (e in node.props.entries) {
            if (e.key != MODEL_PREVIEW_BASE_PROP) {
                applyProp(el, e.key, e.value, ctx)
            }
        }
        // 动态 slot.read 注册：slot 声明了 readId 且有真实容器 source 时，把
        // readId → (source,index) 信号注册进 SlotApi，让 for/数据驱动里的 per-item 槽位
        // 能被循环外 slot.read(readId) 读取；slot dispose 时注销。SlotApi 为全局同一实例，
        // 故此处注册对任意 slot.read 立即可见（NOOP 实现下 register/unregister 为 no-op，优雅降级）。
        if (el is Slot) {
            val slotApi = ctx.evalCtx.slotApi
            val rid = el.readId()
            if (!rid.isNullOrEmpty() && el.groupId().isNotEmpty()) {
                slotApi.register(rid, el.groupId(), el.key())
                // lifecycle.Disposable 是 fun interface，SAM 转换接收 lambda。
                el.lifecycleScope().register { slotApi.unregister(rid) }
            } else if (rid.isNullOrEmpty() &&
                el.groupId().isNotEmpty() &&
                ctx.evalCtx.locals.isEmpty()
            ) {
                // 顶层（非 for/source per-cell）槽 + 表达式 source/index 的兜底注册：
                // 静态收集表（SlotApi.collectSlotViews）只认【字面量】source/index，表达式 index（如
                // index = state.page*7+i）的槽不进表 → slot.read(本元素 id) 恒空（图标却正常，因渲染走
                // 运行期 EXPR 求值、与收集表独立）。这里按元素 DSL id 注册运行期已解析的 (groupId, key)，
                // 让 slot.read(id) 读到本格物品，与字面量槽语义对齐。
                // 仅当 evalCtx 无 cell 形参（getLocals 为空）时注册——for/source per-cell 实例化共享同一
                // 节点 id 且必带 cell locals，故被排除，避免多实例按同一 id 互相覆盖（那本就该用 readId/cell.X）；
                // 漏注册（最坏）只退回原行为（读空），绝不产生错数据。
                val eid = node.id
                if (eid != null && !eid.isEmpty()) {
                    slotApi.register(eid, el.groupId(), el.key())
                    el.lifecycleScope().register { slotApi.unregister(eid) }
                }
            }
        }
        if (el is Toast && node.contentRef != null) {
            el.contentRef(node.contentRef)
        }
        installAnimationOnlyEventTriggers(el, node.props, ctx)
        for ((childIndex, child) in node.children.withIndex()) {
            val childEl: UIElement<*> = buildElement(child, ctx.withChildNode(childIndex, child))
            // $for / $cond 是透明重组容器（kind=$for/$cond → 运行时一个透明 box，已标记 layoutTransparent）。
            // flex（row/column）父下，布局引擎按 display: contents 摊平它、把循环/分支项直接挂到父，
            // 父的 gap/align/justify/wrap 于是真正作用于这些子项（见 LayoutNode.transparentLayout）。
            // 但并非所有父都摊平：摊平与否由父 flattensTransparentChildren() 决定。普通 flex 父摊平后
            // 透明 box 无 Taffy 节点、这些拷贝被旁路；而 ScrollView 等「flex 但不摊平」的父（保护
            // flexShrink=0），以及 STACK/GRID/ABSOLUTE 非 flex 父下，透明 box 仍是真实节点、此拷贝生效，
            // 否则子项退默认值（mode=ROW/gap=0/align=START/justify=START/NOWRAP）。故必须保留此兜底拷贝。
            // GRID 父还需同步拷贝「父级」grid 配置（列数/track-list/自动流向/子项主轴对齐），
            // 否则 $for 拿到 GRID mode 却无 track 定义，track-list 网格 + $for 动态子项会退化为等宽/默认。
            // 仅拷父级配置；gridColumn/gridRow/aspectRatio 是「子项级」字段，属于各子自身，不在此拷贝。
            // padding 作用于容器自身内边距，与透明 box 无继承关系，不在此拷贝。
            if (child.isFor || child.isConditional) {
                childEl.layoutStyle().mode = el.layoutStyle().mode
                childEl.layoutStyle().gap = el.layoutStyle().gap
                childEl.layoutStyle().align = el.layoutStyle().align
                childEl.layoutStyle().justify = el.layoutStyle().justify
                childEl.layoutStyle().flexWrap = el.layoutStyle().flexWrap
                if (el.layoutStyle().mode == LayoutMode.GRID) {
                    childEl.layoutStyle().gridColumns = el.layoutStyle().gridColumns
                    childEl.layoutStyle().gridRows = el.layoutStyle().gridRows
                    childEl.layoutStyle().gridTemplateColumns = el.layoutStyle().gridTemplateColumns
                    childEl.layoutStyle().gridTemplateRows = el.layoutStyle().gridTemplateRows
                    childEl.layoutStyle().gridAutoFlow = el.layoutStyle().gridAutoFlow
                    childEl.layoutStyle().justifyItems = el.layoutStyle().justifyItems
                }
            }
            el.child(childEl)
        }
        if (el.layoutStyle().mode == LayoutMode.STACK) {
            for (child in el.children()) {
                if (child.layoutStyle().positionMode == PositionMode.RELATIVE) {
                    child.positionMode(PositionMode.ABSOLUTE)
                    // 仅在子节点未显式声明 x/y 时补 0；已有显式值则保留。
                    if (child.layoutStyle().x is Dimension.Auto) {
                        child.x(Dimension.px(0f))
                    }
                    if (child.layoutStyle().y is Dimension.Auto) {
                        child.y(Dimension.px(0f))
                    }
                }
            }
        }
        // layout=absolute 父容器：直接子元素自动按 x/y 像素定位（与 CSS position:relative
        // 父 + position:absolute 子语义对齐）。
        // post-order DFS：自身先被构建为 ABSOLUTE，cascade 子节点后再把自身改回 RELATIVE，
        // 让父容器把自己当作流式子元素布局；外层若也是 absolute，会在自己的 buildElement
        // 末尾再次 cascade 把本节点设回 ABSOLUTE。这一来回保证：
        //   - 顶层 absolute root 最终为 RELATIVE，taffy 给它 DSL 声明的尺寸而非 0×0
        //   - 中层 absolute 容器最终为 ABSOLUTE，按 x/y 在外层 absolute 父中定位
        //   - 直接子全部为 ABSOLUTE，按 x/y 在自身坐标系内定位
        if (el.layoutStyle().positionMode == PositionMode.ABSOLUTE) {
            // flex 容器(LayoutMode.ROW / COLUMN)的子应参与流式布局,不能 cascade 成 absolute——
            // 否则 column/row 内的子元素失去流式定位,inset 全 auto 时会堆叠重叠
            // (onboarding 引导对话框的"下一步""跳过引导"按钮挤在一起即此故障)。
            //
            // 注意:这里依赖 LayoutPropContributor 的不变量——
            //   - Box + layout=absolute → mode=ABSOLUTE(子被 cascade,但保留 auto 坐标)
            //   - Column/Row + layout=absolute → 保留固有的 COLUMN/ROW mode(不 cascade,内部流式)
            //   - stack {} kind → mode=STACK(子被 cascade + 补 0,见上面 STACK 分支)
            //   - grid {} / grid+layout=absolute → mode=GRID(子被 cascade,但仍按 grid 算法定位)
            //
            // 设计参考:CSS 的 display 和 position 正交;Taffy 的 Display 与 Position 正交。
            // LayoutMode 在本项目对应"父布局算法",PositionMode 对应"我自己在父里如何定位"——
            // 两个维度独立,cascade 仅依赖 LayoutMode 即可正确决策。
            val mode = el.layoutStyle().mode
            val flexContainer = mode == LayoutMode.ROW || mode == LayoutMode.COLUMN
            if (!flexContainer) {
                for (child in el.children()) {
                    if (child.layoutStyle().positionMode == PositionMode.RELATIVE) {
                        child.positionMode(PositionMode.ABSOLUTE)
                    }
                }
            }
            el.positionMode(PositionMode.RELATIVE)
        }
        return el
    }

    /**
     * M11.7 Stage 1：把 canvas 的 width/height/duration 表达式绑定为响应式 Effect。
     * 三段表达式 blob 任一非 null 时注册一个统一 Effect，在每次 state 变更后
     * 重新求值并通过 [CanvasElement.updateDimensions]
     * 回写。常量字段直接读 IR 里的 width/height/durationMs 占位值。
     */
    private fun bindCanvasDimensions(canvas: CanvasElement, data: UiCanvasData, ctx: IrInterpreter.Context) {
        val widthBlob = data.widthExpr
        val heightBlob = data.heightExpr
        val durationBlob = data.durationExpr
        if (widthBlob == null && heightBlob == null && durationBlob == null) {
            return
        }
        val widthExpr: ExprNode? = if (widthBlob == null) null else ExprBlobReader.readExpr(widthBlob)
        val heightExpr: ExprNode? = if (heightBlob == null) null else ExprBlobReader.readExpr(heightBlob)
        val durationExpr: ExprNode? = if (durationBlob == null) null else ExprBlobReader.readExpr(durationBlob)
        val constWidth = data.width
        val constHeight = data.height
        val constDuration = data.durationMs
        val ef = Effect {
            val widthValue = evaluateCanvasValue(widthExpr, constWidth, ctx)
            val heightValue = evaluateCanvasValue(heightExpr, constHeight, ctx)
            val durationValue = evaluateCanvasValue(durationExpr, constDuration, ctx)
            canvas.updateDimensions(
                toInt(widthValue, constWidth),
                toInt(heightValue, constHeight),
                toDouble(durationValue, constDuration),
            )
            applyCanvasLayoutDimensions(canvas, widthValue, heightValue)
        }
        canvas.lifecycleScope().register(ef)
    }

    /** 求值 canvas 维度表达式；缺省回退字面量 fallback。 */
    private fun evaluateCanvasValue(expr: ExprNode?, fallback: Any, ctx: IrInterpreter.Context): Any? =
        if (expr == null) fallback else ExprEvaluator.eval(expr, ctx.evalCtx)

    /** 字符串（如 "fill"）/null 维度沿用普通元素的布局语义（fill → 100%），数值维度仍走像素。 */
    private fun applyCanvasLayoutDimensions(canvas: CanvasElement, widthValue: Any?, heightValue: Any?) {
        if (widthValue == null || widthValue is String) {
            canvas.layoutStyle().width = ValueCoercion.toDimension(widthValue)
        }
        if (heightValue == null || heightValue is String) {
            canvas.layoutStyle().height = ValueCoercion.toDimension(heightValue)
        }
    }

    /** 将表达式求值结果按数值规则转 int；非数值返回 fallback。 */
    private fun toInt(v: Any?, fallback: Int): Int {
        if (v is Number) return v.toInt()
        if (v is Boolean) return if (v) 1 else 0
        return fallback
    }

    /** 将表达式求值结果按数值规则转 double；非数值返回 fallback。 */
    private fun toDouble(v: Any?, fallback: Double): Double {
        if (v is Number) return v.toDouble()
        if (v is Boolean) return if (v) 1.0 else 0.0
        return fallback
    }

    private fun applyModifiers(el: UIElement<*>, node: UiNode, ctx: IrInterpreter.Context) {
        for (modifier in node.modifiers) {
            applyModifier(el, modifier, ctx)
        }
    }

    private fun applyModifier(el: UIElement<*>, modifier: UiModifier, ctx: IrInterpreter.Context) {
        val condition = modifier.fields["__condition"]
        if (condition != null && condition.type == UiBinding.BindingType.LITERAL && condition.literalValue == false) {
            return
        }
        if (applyMaterialModifier(el, modifier, ctx)) {
            return
        }
        val prefix = if (modifier.kind == "style" || modifier.kind == "surface") "" else "effect.${modifier.kind}."
        for (e in modifier.fields.entries) {
            if (e.key == "__condition") continue
            applyProp(el, prefix + e.key, e.value, ctx)
        }
    }

    private fun applyMaterialModifier(el: UIElement<*>, modifier: UiModifier, ctx: IrInterpreter.Context): Boolean {
        // 先判定 kind 是否 material：非 material 的普通 style / effect modifier 直接返回，
        // 不在此 eager 求值字段。否则 Kotlin 严格实参求值会先跑 materialFields 把所有字段
        // 表达式求值一遍，令普通 modifier 的异步绑定（如 items 未就绪时 state.items[0]）越界后
        // 绕过 applyProp 的 try-catch 容错、直接崩溃整棵 UI 树。
        if (!isMaterialKind(modifier.kind)) {
            return false
        }
        val material = materialModifier(modifier.kind, materialFields(modifier, ctx)) ?: return false
        el.style().addEffectModifier(material)
        el.markRenderDirty()
        return true
    }

    /** material modifier 的 kind 判定：命中白名单才在 [applyMaterialModifier] 里 eager 求值字段。 */
    private fun isMaterialKind(kind: String): Boolean = kind in MATERIAL_KINDS

    private fun materialFields(modifier: UiModifier, ctx: IrInterpreter.Context): Map<String, Any?> = modifier.fields
        .filterKeys { it != "__condition" }
        .mapValues { readCalleeFromBinding(it.value, ctx) }

    /**
     * material modifier 的 kind 全集。与下方 [materialModifier] 的 when 分支一一对应，
     * 新增 / 删除 material kind 时须同步修改本集合与 when 两处（when 的 else→null 为第二道防线）。
     */
    private val MATERIAL_KINDS: Set<String> = setOf(
        "textureLayer",
        "materialOverlay",
        "historyLayer",
        "wornFrame",
        "runeOverlay",
        "materialGraph",
        "shaderLayer",
        "particleLayer",
    )

    private fun materialModifier(kind: String, fields: Map<String, Any?>): VisualModifier? = when (kind) {
        "textureLayer" -> VisualModifier.TextureLayer(
            stringField(fields, "source", ""),
            stringField(fields, "sampling", "stretch"),
            fields["uv"]?.toString(),
            floatField(fields, "scale", 1f),
            colorField(fields, "tint", 0xFFFFFFFF.toInt()),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "normal"),
            fields["nineSlice"]?.toString() ?: fields["nine_slice"]?.toString(),
        )
        "materialOverlay" -> VisualModifier.MaterialOverlay(
            stringField(fields, "material", "noise"),
            intField(fields, "seed", 0),
            floatField(fields, "scale", 1f),
            floatField(fields, "roughness", 0f),
            colorField(fields, "tint", 0xFFFFFFFF.toInt()),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "normal"),
        )
        "historyLayer" -> VisualModifier.HistoryLayer(
            stringField(fields, "source", "self"),
            intField(fields, "frames", 1),
            intField(fields, "downsample", 1),
            floatField(fields, "decay", 0f),
            floatField(fields, "blur", 0f),
            floatField(fields, "distortion", 0f),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "normal"),
            fields["trigger"]?.toString(),
        )
        "wornFrame" -> VisualModifier.WornFrame(
            floatField(fields, "thickness", 1f),
            floatField(fields, "erosion", 0f),
            floatField(fields, "roughness", 0f),
            floatField(fields, "edgeStrength", 1f),
            colorField(fields, "tint", 0xFFFFFFFF.toInt()),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "normal"),
        )
        "runeOverlay" -> VisualModifier.RuneOverlay(
            stringField(fields, "runeSet", "nordic"),
            intField(fields, "seed", 0),
            floatField(fields, "glow", 0f),
            floatField(fields, "density", 1f),
            colorField(fields, "tint", 0xFFFFFFFF.toInt()),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "normal"),
        )
        "materialGraph" -> VisualModifier.MaterialGraph(
            stringField(fields, "graph", ""),
            intField(fields, "budget", 0),
            fields["fallback"]?.toString(),
        )
        "shaderLayer" -> VisualModifier.ShaderLayer(
            stringField(fields, "program", ""),
            shaderParams(fields),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "normal"),
            fields["fallback"]?.toString(),
        )
        "particleLayer" -> VisualModifier.ParticleLayer(
            stringField(fields, "kind", "snow"),
            stringField(fields, "emission", "continuous"),
            stringField(fields, "layer", "front"),
            floatField(fields, "rate", 20f),
            intField(fields, "count", 24),
            fields["trigger"]?.toString(),
            floatField(fields, "lifetime", 2f),
            floatField(fields, "gravity", 40f),
            floatField(fields, "wind", 0f),
            floatField(fields, "speed", 30f),
            floatField(fields, "size", 3f),
            colorField(fields, "colorA", 0xFFFFFFFF.toInt()),
            colorField(fields, "colorB", 0xFFFFFFFF.toInt()),
            floatField(fields, "glow", 0.6f),
            floatField(fields, "bounce", 0.3f),
            floatField(fields, "hoverForce", 0f),
            floatField(fields, "opacity", 1f),
            stringField(fields, "blend", "additive"),
            intField(fields, "seed", 0),
            // FR-UI-032 发射形状与力场（未声明时全部走默认，向后兼容）。
            fields["emitShape"]?.toString()?.takeIf { it.isNotBlank() },
            stringField(fields, "emitEdge", "top"),
            floatField(fields, "emitAngle", Float.NaN),
            floatField(fields, "emitSpread", 30f),
            boolField(fields, "emitInward", false),
            floatField(fields, "noiseStrength", 0f),
            floatField(fields, "noiseFrequency", 1f),
            floatField(fields, "attractX", 0.5f),
            floatField(fields, "attractY", 0.5f),
            floatField(fields, "attractStrength", 0f),
            floatField(fields, "drag", 0f),
            // FR-UI-033 over-lifetime 曲线原始停靠点表（[[t, value], ...]，解析与诊断在中立粒子层）。
            fields["colorOverLifetime"] as? List<*>,
            fields["sizeOverLifetime"] as? List<*>,
            fields["alphaOverLifetime"] as? List<*>,
            fields["rotationOverLifetime"] as? List<*>,
            // FR-UI-034 贴图粒子 / 序列帧 / 拖尾（未声明时程序化柔光圆点、无序列帧、无拖尾）。
            fields["texture"]?.toString()?.takeIf { it.isNotBlank() },
            intField(fields, "sheetRows", 1),
            intField(fields, "sheetCols", 1),
            floatField(fields, "sheetFps", 0f),
            intField(fields, "trailLength", 0),
            floatField(fields, "trailFade", 1f),
        )
        else -> null
    }

    private fun shaderParams(fields: Map<String, Any?>): Map<String, Any?> = fields
        .filterKeys { it.startsWith("params.") }
        .mapKeys { it.key.removePrefix("params.") }

    private fun stringField(fields: Map<String, Any?>, key: String, defaultValue: String): String =
        fields[key]?.toString()?.takeIf { it.isNotBlank() } ?: defaultValue

    private fun intField(fields: Map<String, Any?>, key: String, defaultValue: Int): Int =
        when (val value = fields[key]) {
            is Number -> value.toInt()
            is String -> value.trim().toIntOrNull() ?: defaultValue
            else -> defaultValue
        }

    private fun floatField(fields: Map<String, Any?>, key: String, defaultValue: Float): Float =
        when (val value = fields[key]) {
            is Number -> value.toFloat()
            is String -> value.trim().toFloatOrNull() ?: defaultValue
            else -> defaultValue
        }

    private fun boolField(fields: Map<String, Any?>, key: String, defaultValue: Boolean): Boolean =
        when (val value = fields[key]) {
            is Boolean -> value
            is String -> value.trim().equals("true", ignoreCase = true)
            else -> defaultValue
        }

    private fun colorField(fields: Map<String, Any?>, key: String, defaultValue: Int): Int =
        runCatching { ValueCoercion.toIntColor(fields[key]) }.getOrDefault(defaultValue)

    @Suppress("ReturnCount")
    private fun applyProp(el: UIElement<*>, key: String, binding: UiBinding, ctx: IrInterpreter.Context) {
        // M6-i：`<trigger>Complete` 是 on { trigger = "anim" -> ::fn } 编译产物，
        // 把回调绑定到元素上，等待 scheduler.play 返回 handle 时再注入 onComplete。
        if (isTriggerCompleteKey(el, key)) {
            installTriggerOnComplete(el, key, binding, ctx)
            return
        }
        when (binding.type) {
            UiBinding.BindingType.LITERAL -> {
                applyValue(el, key, binding.literalValue, ctx)
                return
            }
            UiBinding.BindingType.EXPR -> {
                // EXPR 绑定保证 exprBytes 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
                val expr: ExprNode = ExprBlobReader.readExpr(binding.exprBytes!!)
                if (bindStateBackedControl(el, key, expr, ctx)) {
                    return
                }
                // Dropdown items / options：表达式求值为 List，响应式重建 options 列表
                if (("items" == key || "options" == key) && el is Dropdown) {
                    val itemsEffect = Effect {
                        try {
                            val raw: Any? = ExprEvaluator.eval(expr, ctx.evalCtx)
                            el.rebuildOptions(buildOptionsFromItems(raw))
                        } catch (e: RuntimeException) {
                            // dropdown items 表达式求值失败属高频降级，详细诊断时再打
                            if (uiDebugEnabled()) {
                                LOGGER.debug("[ui] dropdown items 表达式求值失败：${e.message}")
                            }
                        }
                    }
                    el.lifecycleScope().register(itemsEffect)
                    return
                }
                val ef = Effect {
                    try {
                        val (value, themeDependent) = ThemeEnvironment.trackLookupUsage {
                            ExprEvaluator.eval(expr, ctx.evalCtx)
                        }
                        applyReactiveValue(el, key, value, ctx, themeDependent)
                    } catch (e: DslRuntimeError) {
                        logBindingFailureOnce(ctx.screenId, el, key, e.message)
                    } catch (e: RuntimeException) {
                        logBindingFailureOnce(ctx.screenId, el, key, e.message)
                    }
                }
                el.lifecycleScope().register(ef)
                return
            }
            UiBinding.BindingType.EVENT -> {
                // EVENT 绑定由 UiBinding.event() 工厂构造，eventActionName 恒非空；
                // 转 Kotlin 后 getter 由平台类型收窄为 String?，此处 !! 复刻旧 Java 非空传递语义。
                bindEvent(el, key, binding.eventActionName!!, ctx)
                return
            }
            else -> error("未知绑定类型: ${binding.type}")
        }
    }

    /**
     * M6-i：判断 prop key 是否是某触发器的 onComplete 回调入口
     * （形如 `<trigger>Complete`，其中 `<trigger>` 必须已在元素上声明动画引用）。
     */
    private fun isTriggerCompleteKey(el: UIElement<*>, key: String?): Boolean {
        if (key == null || !key.endsWith("Complete") || key.length <= "Complete".length) {
            return false
        }
        val trigger = key.substring(0, key.length - "Complete".length)
        return el.animationFor(trigger) != null
    }

    /**
     * M6-i：把 `<trigger>Complete` 绑定的 FunRef / BoundFunRef snapshot 求值，
     * 转换成无参 EffectBody 并登记到元素 animationCompleteCallback 表。
     */
    private fun installTriggerOnComplete(
        el: UIElement<*>,
        key: String,
        binding: UiBinding,
        ctx: IrInterpreter.Context,
    ) {
        val trigger = key.substring(0, key.length - "Complete".length)
        val callee: Any? = readCalleeFromBinding(binding, ctx)
        if (callee == null) {
            // onComplete 回调缺失属可忽略降级，详细诊断时再打
            if (uiDebugEnabled()) {
                LOGGER.debug("[ui] on $trigger 的 onComplete 回调求值为 null，已忽略")
            }
            return
        }
        val runnable = EffectBody {
            try {
                CallDispatcher.invokeCallable(
                    callee,
                    emptyList<Any?>(),
                    ctx.evalCtx,
                    "on $trigger onComplete",
                )
            } catch (e: RuntimeException) {
                // onComplete 回调执行失败属可忽略降级，详细诊断时再打
                if (uiDebugEnabled()) {
                    LOGGER.debug("[ui] on $trigger 的 onComplete 回调执行失败：$e")
                }
            }
        }
        el.animationCompleteCallback(trigger, runnable)
    }

    /** 把 onComplete 绑定的 UiBinding（LITERAL/EXPR）求值为可调用对象。 */
    private fun readCalleeFromBinding(binding: UiBinding, ctx: IrInterpreter.Context): Any? = when (binding.type) {
        UiBinding.BindingType.LITERAL -> binding.literalValue
        UiBinding.BindingType.EXPR -> {
            // EXPR 绑定保证 exprBytes 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
            val expr: ExprNode = ExprBlobReader.readExpr(binding.exprBytes!!)
            ExprEvaluator.eval(expr, ctx.evalCtx)
        }
        else -> null
    }

    /** 将求值后的值写入对应属性。具体属性映射见 [PropAppliers]。 */
    private fun applyValue(el: UIElement<*>, key: String, v: Any?, ctx: IrInterpreter.Context) {
        // M6-e：事件 prop（onClick 等）的 EXPR binding（如 ::fun(args) 部分应用 / lambda）
        // 在 codegen 阶段因 eventEntryName 返回 null 而走 EXPR 路径；runtime 求值后得到
        // 可调用对象（BoundFunRefValue / LambdaValue / FunRefValue），需注册为事件 handler
        // 而不是走 PropAppliers（PropAppliers 没有 onClick 等 key 的注册）。
        if (isEventPropKey(key) && isCallable(v)) {
            installExprEventHandler(el, key, v, ctx)
            return
        }
        PropAppliers.apply(el, key, v, propLocationLabel(el, key), ctx.evalCtx.themeLookup)
        rememberValue(el, key, v, ctx)
    }

    /** 阶段补丁：拼出 "elementId.key" 作为 PropAppliers WARN 定位标签；id 为空时退化为类名。 */
    private fun propLocationLabel(el: UIElement<*>?, key: String): String {
        if (el == null) return key
        val id = el.id()
        val head = if (id == null || id.isEmpty()) el::class.simpleName else id
        return "$head.$key"
    }

    /** 事件 prop key 集合（与编译器 EventProps.NAMES 保持同步）。 */
    @Suppress("ComplexCondition", "CyclomaticComplexMethod")
    private fun isEventPropKey(key: String): Boolean = key == "onClick" ||
        key == "onChange" ||
        key == "onSubmit" ||
        key == "onRightClick" ||
        key == "onLongPress" ||
        key == "onDoubleClick" ||
        key == "onDragEnter" ||
        key == "onDragLeave" ||
        key == "onHover" ||
        key == "interceptClick" ||
        key == "interceptRightClick" ||
        key == "interceptDrop" ||
        key == "onDismiss" ||
        key == "onRefresh" ||
        key == "onLoadMore" ||
        key == "onReorder" ||
        key == "onDragStart" ||
        key == "onDragOver" ||
        key == "onDrop" ||
        // v4 C.2：通用拖拽 / 指针移动 / 滚轮事件
        key == "onDrag" ||
        key == "onDragEnd" ||
        key == "onPointerMove" ||
        key == "onWheel"

    /** 判断 runtime 值是否为可调用对象。 */
    private fun isCallable(v: Any?): Boolean = v is BoundFunRefValue ||
        v is LambdaValue ||
        v is FunRefValue ||
        v is LocalFunValue

    /** 把可调用对象注册为元素的事件 handler（委托给 EventBindingDispatcher）。 */
    private fun installExprEventHandler(el: UIElement<*>, key: String, callable: Any?, ctx: IrInterpreter.Context) {
        EventBindingDispatcher.installExprEventHandler(el, key, callable, ctx)
    }

    private fun applyReactiveValue(
        el: UIElement<*>,
        key: String,
        v: Any?,
        ctx: IrInterpreter.Context,
        themeDependent: Boolean,
    ) {
        val previous: Any? = rememberedValue(el, key, ctx)
        if (!themeDependent && previous != null && previous != v) {
            val transition = el.animationFor("transition")
            val scheduler = el.animationScheduler() as AnimationScheduler? // marker 断环，见 AnimationDriverHandle
            if (transition != null && scheduler != null && canInterpolate(key, previous, v)) {
                scheduler.transition(el, key, currentRuntimeValue(el, key, previous), v, transition)
                rememberValue(el, key, v, ctx)
                return
            }
        }
        applyValue(el, key, v, ctx)
    }

    /** 判断属性是否能在 previous → v 之间补间。数值类型直接 lerp；颜色属性按 ARGB 通道 lerp。 */
    private fun canInterpolate(key: String, previous: Any?, v: Any?): Boolean {
        if (previous is Number && v is Number) {
            return true
        }
        return ("background" == key || "color" == key) &&
            (previous is Number || previous is String) &&
            (v is Number || v is String)
    }

    private fun rememberedValue(el: UIElement<*>, key: String, ctx: IrInterpreter.Context): Any? {
        val values = ctx.lastValues[el]
        return values?.get(key)
    }

    private fun rememberValue(el: UIElement<*>, key: String, value: Any?, ctx: IrInterpreter.Context) {
        ctx.lastValues.getOrPut(el) { LinkedHashMap() }[key] = value
    }

    private fun currentRuntimeValue(el: UIElement<*>, key: String, fallback: Any?): Any? {
        val value: Any? = PropAppliers.read(el, key)
        return value ?: fallback
    }

    @Suppress("ReturnCount", "CyclomaticComplexMethod")
    private fun bindStateBackedControl(
        el: UIElement<*>,
        key: String,
        expr: ExprNode,
        ctx: IrInterpreter.Context,
    ): Boolean {
        if (expr !is ExprNode.StateRef) {
            return false
        }
        val signal: Signal<Any?> = ctx.states[expr.name] ?: return false
        if ("value" == key && el is Slider) {
            bindStateBackedSliderValue(el, signal, ctx)
            return true
        }
        if ("checked" == key && el is Checkbox) {
            el.bindValue(signal)
            return true
        }
        if ("enabled" == key && el is Switch) {
            el.bindEnabled(signal)
            return true
        }
        if ("selectedValue" == key && el is Dropdown) {
            el.bindSelectedValue(signal)
            return true
        }
        if ("selectedValue" == key && el is RadioGroup) {
            el.bindSelectedValue(signal)
            return true
        }
        if ("value" == key && el is TextField) {
            el.bindValue(signal)
            return true
        }
        if ("activeIndex" == key && el is TabView) {
            el.bindActiveIndex(signal)
            return true
        }
        // 特性①:scrollY/scrollX = state.x 双向绑定(滚动→state 回写 + state→滚动驱动)。
        if ("scrollY" == key && el is ScrollableContainer<*>) {
            el.bindScrollY(signal)
            return true
        }
        if ("scrollX" == key && el is ScrollableContainer<*>) {
            el.bindScrollX(signal)
            return true
        }
        return false
    }

    private fun bindEvent(el: UIElement<*>, key: String, actionName: String, ctx: IrInterpreter.Context) {
        EventBindingDispatcher.bindEvent(el, key, actionName, ctx)
    }

    private fun installAnimationOnlyEventTriggers(
        el: UIElement<*>,
        props: Map<String, UiBinding>,
        ctx: IrInterpreter.Context,
    ) {
        EventBindingDispatcher.installAnimationOnlyEventTriggers(el, props, ctx)
    }

    // ====== Dropdown items 表达式 → DropdownOption 列表 ======

    /**
     * 将 items 表达式求值结果转换为 DropdownOption 列表。
     *
     * 支持两种格式：
     * - `List<String>`：value 和 label 相同
     * - `List<Map<String, Object>>`：包含 "value" 和可选 "label" 键
     */
    private fun buildOptionsFromItems(raw: Any?): List<DropdownOption> {
        val result: MutableList<DropdownOption> = ArrayList()
        if (raw is List<*>) {
            for (item in raw) {
                if (item is Map<*, *>) {
                    val value = if (item["value"] != null) item["value"].toString() else ""
                    val label = if (item["label"] != null) item["label"].toString() else ""
                    val opt = DropdownOption.create()
                    opt.value(value)
                    opt.label(label)
                    result.add(opt)
                } else {
                    val v = if (item != null) item.toString() else ""
                    val opt = DropdownOption.create()
                    opt.value(v)
                    result.add(opt)
                }
            }
        }
        return result
    }

    private fun bindStateBackedSliderValue(slider: Slider, signal: Signal<Any?>, ctx: IrInterpreter.Context) {
        ctx.sliderBindings.bindStateBackedValue(slider, signal)
    }

    // 忠实保留原 Java 中存在但 IrInterpreter 内未被调用的私有委托方法（实际事件动画播放走 EventBindingDispatcher）。
    @Suppress("UnusedPrivateMember")
    private fun bindSliderChangeTrigger(slider: Slider, trigger: UiTask, ctx: IrInterpreter.Context) {
        ctx.sliderBindings.bindChangeTrigger(slider, trigger)
    }

    @Suppress("UnusedPrivateMember")
    private fun playEventAnimations(el: UIElement<*>, eventKey: String) {
        EventBindingDispatcher.playEventAnimations(el, eventKey)
    }

    @Suppress("UnusedPrivateMember")
    private fun playAnimation(el: UIElement<*>, trigger: String) {
        EventBindingDispatcher.playAnimation(el, trigger)
    }

    // ====== M6-c：数据驱动容器（source = list/map） ======

    /**
     * 构造数据驱动容器：外壳走普通 create + 容器自身属性求值一次（不含 children），
     * 然后挂一个反应式 [Effect]，每次 source 表达式发生变化重新 diff children。
     *
     * 容器自身属性按 RFC §5.5 仅在创建期求值一次；模板形参（item/key）不在外壳作用域内。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildDataDrivenContainer(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val container: UIElement<*> = IrElementFactory.create(node)
        container.id(node.id)
        // M6-i：与 buildElement 保持一致——动画引用先于 props 装配。
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) {
                container.animationRef(e.key, animation)
            }
        }
        applyModifiers(container, node, ctx)
        for (e in node.props.entries) {
            applyProp(container, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(container, node.props, ctx)

        // 数据驱动容器保证 sourceExpr 非 null（hasSource 已判定）；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
        val sourceExprNode: ExprNode = ExprBlobReader.readExpr(node.sourceExpr!!)
        val effect = Effect {
            // 单次 source 求值或 diff 异常不应使容器进入不可恢复状态：
            // 捕获后写日志并把容器子节点清空，下一次 source 变化仍可重建。
            try {
                val sourceValue: Any? = ExprEvaluator.eval(sourceExprNode, ctx.evalCtx)
                DataDrivenDiffer.rebuildChildren(container, node, sourceValue, ctx, ::buildElement)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] 数据驱动容器 ${node.id} source 重建失败：$t")
                DataDrivenDiffer.disposeAllDataDrivenChildren(container, ctx)
                container.replaceChildren(emptyList<UIElement<*>>())
            }
        }
        container.lifecycleScope().register(effect)
        // 容器自身释放时清理其 dataDrivenChildren 条目，防止嵌套数据驱动容器孤儿泄漏
        DataDrivenDiffer.registerCtxMapCleanup(container, ctx)
        return container
    }

    // ====== VirtualList：窗口化渲染 ======

    /**
     * 构建 VirtualList 元素：应用 props 后注册 WindowMaterializer 回调，
     * 滚动或 itemCount 变化时按窗口范围 diff 实例化/回收子节点。
     */
    private fun buildVirtualList(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val vl = VirtualList.create()
        vl.id(node.id)
        // 动画引用
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) {
                vl.animationRef(e.key, animation)
            }
        }
        // 应用 props（itemHeight / itemCount / width / height 等）
        applyModifiers(vl, node, ctx)
        for (e in node.props.entries) {
            applyProp(vl, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(vl, node.props, ctx)

        // children 作为模板存储，不立即实例化
        val templates: List<UiNode> = node.children
        // 缓存已实例化的子元素（按 index 为 key）
        val cache: MutableMap<Int, UIElement<*>> = LinkedHashMap()

        vl.materializeWindow { list, first, last ->
            if (first > last) {
                // 清空
                for (child in cache.values) {
                    LifecycleUtils.disposeRecursively(child)
                }
                cache.clear()
                list.setWindowChildren(emptyList())
                return@materializeWindow
            }
            // 回收窗口外的子元素
            cache.entries.removeAll { entry ->
                if (entry.key < first || entry.key > last) {
                    LifecycleUtils.disposeRecursively(entry.value)
                    true
                } else {
                    false
                }
            }
            // 实例化窗口内缺失的子元素
            val window: MutableList<UIElement<*>> = ArrayList(last - first + 1)
            for (i in first..last) {
                var el = cache[i]
                if (el == null) {
                    el = DataDrivenDiffer.instantiateVirtualItem(templates, i, ctx, ::buildElement)
                    cache[i] = el
                    // 动画调度器 attach
                    if (list.animationScheduler() != null) {
                        el.attachAnimationScheduler(list.animationScheduler())
                    }
                }
                window.add(el)
            }
            list.setWindowChildren(window)
        }

        // 触发首次 materialize
        vl.viewportHeight(0f)
        return vl
    }

    // ====== VirtualGrid：二维等大格子窗口化 ======

    private fun buildVirtualGrid(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val grid = VirtualGrid.create()
        grid.id(node.id)
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) grid.animationRef(e.key, animation)
        }
        applyModifiers(grid, node, ctx)
        for (e in node.props.entries) {
            applyProp(grid, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(grid, node.props, ctx)

        val templates: List<UiNode> = node.children
        val cache: MutableMap<Int, UIElement<*>> = LinkedHashMap()
        grid.materializeWindow { g, first, last ->
            if (first > last) {
                cache.values.forEach { LifecycleUtils.disposeRecursively(it) }
                cache.clear()
                g.setWindowChildren(emptyList())
                return@materializeWindow
            }
            cache.entries.removeAll { entry ->
                if (entry.key < first || entry.key > last) {
                    LifecycleUtils.disposeRecursively(entry.value)
                    true
                } else {
                    false
                }
            }
            val window: MutableList<UIElement<*>> = ArrayList(last - first + 1)
            for (i in first..last) {
                var el = cache[i]
                if (el == null) {
                    val locals = LinkedHashMap<String, Any?>()
                    locals["index"] = i
                    el = DataDrivenDiffer.instantiateVirtualItem(templates, locals, ctx, ::buildElement)
                    // 绝对定位到格子坐标（相对内容原点）。
                    el.positionMode(PositionMode.ABSOLUTE)
                    el.x(Dimension.px(g.cellX(i)))
                    el.y(Dimension.px(g.cellY(i)))
                    el.width(Dimension.px(g.itemWidth()))
                    el.height(Dimension.px(g.itemHeight()))
                    cache[i] = el
                    if (g.animationScheduler() != null) el.attachAnimationScheduler(g.animationScheduler())
                }
                window.add(el)
            }
            g.setWindowChildren(window)
        }
        grid.viewportWidth(0f)
        return grid
    }

    // ====== VirtualMasonry：瀑布流窗口化 ======

    private fun buildVirtualMasonry(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val masonry = VirtualMasonry.create()
        masonry.id(node.id)
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) masonry.animationRef(e.key, animation)
        }
        applyModifiers(masonry, node, ctx)
        for (e in node.props.entries) {
            applyProp(masonry, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(masonry, node.props, ctx)

        val templates: List<UiNode> = node.children
        val cache: MutableMap<Int, UIElement<*>> = LinkedHashMap()
        masonry.materializeWindow { mas, first, last ->
            if (first > last) {
                cache.values.forEach { LifecycleUtils.disposeRecursively(it) }
                cache.clear()
                mas.setWindowChildren(emptyList())
                return@materializeWindow
            }
            cache.entries.removeAll { entry ->
                if (entry.key < first || entry.key > last) {
                    LifecycleUtils.disposeRecursively(entry.value)
                    true
                } else {
                    false
                }
            }
            val window: MutableList<UIElement<*>> = ArrayList(last - first + 1)
            for (i in first..last) {
                var el = cache[i]
                if (el == null) {
                    val locals = LinkedHashMap<String, Any?>()
                    locals["index"] = i
                    el = DataDrivenDiffer.instantiateVirtualItem(templates, locals, ctx, ::buildElement)
                    el.positionMode(PositionMode.ABSOLUTE)
                    el.x(Dimension.px(mas.itemX(i)))
                    el.y(Dimension.px(mas.itemY(i)))
                    el.width(Dimension.px(mas.columnWidth()))
                    el.height(Dimension.px(mas.itemHeight(i)))
                    cache[i] = el
                    if (mas.animationScheduler() != null) el.attachAnimationScheduler(mas.animationScheduler())
                }
                window.add(el)
            }
            mas.setWindowChildren(window)
        }
        masonry.viewportWidth(0f)
        return masonry
    }

    // ====== VirtualTree：可折叠树窗口化 ======

    @Suppress("NestedBlockDepth", "CyclomaticComplexMethod", "LongMethod")
    private fun buildVirtualTree(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val tree = VirtualTree.create()
        tree.id(node.id)
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) tree.animationRef(e.key, animation)
        }
        // treeData 为表达式时注册 Effect 把求值结果（List of Map）转 TreeNode 写入 roots；
        // 其余 prop（itemHeight / indentPerDepth / buffer / direction）正常应用。
        applyModifiers(tree, node, ctx)
        var treeDataBinding: UiBinding? = null
        for (e in node.props.entries) {
            if ("treeData" == e.key) {
                treeDataBinding = e.value
                continue
            }
            applyProp(tree, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(tree, node.props, ctx)

        val templates: List<UiNode> = node.children
        val cache: MutableMap<Int, UIElement<*>> = LinkedHashMap()
        // 修复"虚拟树点击弹重复项目":历史实现 cache 按 index 缓存元素,但 toggleExpand
        // 后 flattened rows 顺序与内容都变(子节点插入/删除),同一 index 槽位的旧元素
        // 仍被复用,locals(nodeId/depth/isExpanded)是首次创建时的 → 屏幕看到错位/重复行。
        // 修法:每次 materialize 比对 lastNodeIds(上次 flatten 的 nodeId 序列)与当前
        // 的 rows.node.id() 序列;不一致(toggle / treeData 变化)→ 清空整个 cache 全重建,
        // 保证 locals 总是新鲜。普通滚动 rows 不变 → 保留 cache → 性能不退化。
        val lastNodeIds: MutableList<String> = ArrayList()
        tree.materializeWindow { t, first, last ->
            if (first > last) {
                cache.values.forEach { LifecycleUtils.disposeRecursively(it) }
                cache.clear()
                lastNodeIds.clear()
                t.setWindowChildren(emptyList())
                return@materializeWindow
            }
            val rows: List<VirtualTree.FlatRow> = t.flattenedItems()
            DataDrivenDiffer.validateVirtualTreeBusinessKeys(templates, rows, t.id())
            // 检测 flatten 变化:序列长度或任一 nodeId 顺序不同视为"结构变化",清缓存全重建。
            var flattenChanged = rows.size != lastNodeIds.size
            if (!flattenChanged) {
                for (i in rows.indices) {
                    // FlatRow/TreeNode 已是 Kotlin data class（访问器经 @get:JvmName 保留 Java 侧名）；Kotlin 侧按属性访问。
                    if (rows[i].node.id != lastNodeIds[i]) {
                        flattenChanged = true
                        break
                    }
                }
            }
            if (flattenChanged) {
                cache.values.forEach { LifecycleUtils.disposeRecursively(it) }
                cache.clear()
                lastNodeIds.clear()
                for (r in rows) lastNodeIds.add(r.node.id)
            }
            cache.entries.removeAll { entry ->
                if (entry.key < first || entry.key > last) {
                    LifecycleUtils.disposeRecursively(entry.value)
                    true
                } else {
                    false
                }
            }
            val window: MutableList<UIElement<*>> = ArrayList(last - first + 1)
            for (i in first..last) {
                var el = cache[i]
                if (el == null && i < rows.size) {
                    val row = rows[i]
                    val locals = LinkedHashMap<String, Any?>()
                    locals["index"] = i
                    locals["depth"] = row.depth
                    locals["isLeaf"] = row.node.isLeaf
                    locals["isExpanded"] = t.isExpanded(row.node.id)
                    locals["nodeId"] = row.node.id
                    locals["nodeData"] = row.node.data
                    el = DataDrivenDiffer.instantiateVirtualItem(templates, locals, ctx, ::buildElement)
                    cache[i] = el
                    if (t.animationScheduler() != null) el.attachAnimationScheduler(t.animationScheduler())
                }
                if (el != null) window.add(el)
            }
            t.setWindowChildren(window)
        }

        // treeData 表达式 → roots（Effect 跟踪依赖，变化时重算）。
        if (treeDataBinding != null) {
            bindTreeData(tree, treeDataBinding, ctx)
        } else {
            tree.viewportHeight(0f)
        }
        return tree
    }

    /** 把 treeData 表达式求值结果（List of Map）转成 TreeNode 列表并写入 VirtualTree。 */
    @Suppress("TooGenericExceptionCaught", "ReturnCount")
    private fun bindTreeData(tree: VirtualTree, binding: UiBinding, ctx: IrInterpreter.Context) {
        if (binding.type == UiBinding.BindingType.LITERAL) {
            tree.roots(DataDrivenDiffer.toTreeNodes(binding.literalValue))
            return
        }
        if (binding.type != UiBinding.BindingType.EXPR) {
            return
        }
        // EXPR 绑定保证 exprBytes 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
        val expr: ExprNode = ExprBlobReader.readExpr(binding.exprBytes!!)
        val effect = Effect {
            try {
                val value: Any? = ExprEvaluator.eval(expr, ctx.evalCtx)
                tree.roots(DataDrivenDiffer.toTreeNodes(value))
            } catch (t: Throwable) {
                LOGGER.warn("[ui] VirtualTree ${tree.id()} treeData 重建失败：$t")
                tree.roots(emptyList())
            }
        }
        tree.lifecycleScope().register(effect)
    }

    // ====== MultiColumnVirtualList：多列虚拟列表 ======

    private fun buildMultiColumnList(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val table = MultiColumnVirtualList.create()
        table.id(node.id)
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) table.animationRef(e.key, animation)
        }
        applyModifiers(table, node, ctx)
        for (e in node.props.entries) {
            applyProp(table, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(table, node.props, ctx)

        val templates: List<UiNode> = node.children
        val cache: MutableMap<Int, UIElement<*>> = LinkedHashMap()
        table.materializeWindow { tbl, first, last ->
            if (first > last) {
                cache.values.forEach { LifecycleUtils.disposeRecursively(it) }
                cache.clear()
                tbl.setWindowChildren(emptyList())
                return@materializeWindow
            }
            cache.entries.removeAll { entry ->
                if (entry.key < first || entry.key > last) {
                    LifecycleUtils.disposeRecursively(entry.value)
                    true
                } else {
                    false
                }
            }
            val window: MutableList<UIElement<*>> = ArrayList(last - first + 1)
            for (i in first..last) {
                var el = cache[i]
                if (el == null) {
                    val locals = LinkedHashMap<String, Any?>()
                    locals["index"] = i
                    el = DataDrivenDiffer.instantiateVirtualItem(templates, locals, ctx, ::buildElement)
                    cache[i] = el
                    if (tbl.animationScheduler() != null) el.attachAnimationScheduler(tbl.animationScheduler())
                }
                window.add(el)
            }
            tbl.setWindowChildren(window)
        }
        table.viewportHeight(0f)
        return table
    }

    // ====== PageView / Carousel：分页容器 ======

    @Suppress("ComplexCondition")
    private fun buildPageView(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val carousel = "carousel" == node.kind
        val page: PageView = if (carousel) CarouselView.create() else PageView.create()
        page.id(node.id)
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) page.animationRef(e.key, animation)
        }
        applyModifiers(page, node, ctx)
        // 先记录 DSL 显式声明的 currentPage 字面量,在 children 装入后再 set 一次,
        // 避免 setCurrentPage 在 children=0 时被 clampPage(p, 0)=0 截断到第 0 页。
        var initialPageOverride: Int? = null
        for (e in node.props.entries) {
            val b = e.value
            val literalValue = b?.literalValue
            if ("currentPage" == e.key &&
                b != null &&
                b.type == UiBinding.BindingType.LITERAL &&
                literalValue is Number
            ) {
                initialPageOverride = literalValue.toInt()
            }
            applyProp(page, e.key, b, ctx)
        }
        installAnimationOnlyEventTriggers(page, node.props, ctx)

        // 每个直接子节点即一页，eager 实例化并强制占满视窗（100% 宽高；百分比约定 0~100）。
        for ((childIndex, childNode) in node.children.withIndex()) {
            val child: UIElement<*> = buildElement(childNode, ctx.withChildNode(childIndex, childNode))
            child.width(Dimension.percent(100f))
            child.height(Dimension.percent(100f))
            child.layoutStyle().flexShrink = 0f
            page.child(child)
        }
        // children 已装入,pageCount() 此时返回真实值;重新 setCurrentPage 让 clampPage
        // 用真实 pageCount 不再截到 0。真正 snap scroll 偏移由 PageView.viewportWidth/Height
        // setter 在首帧 layout 时 wasZero→非0 触发(IrInterpreter 阶段 viewport 仍是 0,
        // setCurrentPage 内的 snapToCurrentPage 会算出 scrollX = N * 0 = 0)。
        if (initialPageOverride != null) {
            page.setCurrentPage(initialPageOverride)
        }
        return page
    }

    // ====== Phase 3 C 类装饰器:包裹型容器 ======

    private fun buildScrollDecorator(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val decorator: ScrollDecorator<*> = IrElementFactory.create(node.kind) as ScrollDecorator<*>
        decorator.id(node.id)
        for (e in node.animationRefs.entries) {
            val animation = ctx.animations[e.value]
            if (animation != null) decorator.animationRef(e.key, animation)
        }
        applyModifiers(decorator, node, ctx)
        for (e in node.props.entries) {
            applyProp(decorator, e.key, e.value, ctx)
        }
        installAnimationOnlyEventTriggers(decorator, node.props, ctx)
        // 子节点正常实例化;首个 ScrollableContainer 子设为 body(供装饰器读取滚动状态)。
        for ((childIndex, childNode) in node.children.withIndex()) {
            val child: UIElement<*> = buildElement(childNode, ctx.withChildNode(childIndex, childNode))
            decorator.child(child)
            if (decorator.body() == null && child is ScrollableContainer<*>) {
                decorator.bodyInternal(child)
            }
        }
        return decorator
    }

    // ====== $for：元素树位置 for 循环重组（kind = "$for"） ======

    /**
     * 构造 for 容器：包装一个透明 Box，注册 [Effect] 跟踪 iterable 表达式依赖的 Signal；
     * iterable 变化时按 key 增量 diff 子元素列表——复用 M6 的反应式重建机制
     * （[DataDrivenDiffer.extractListKey] keyed diff +
     * [DataDrivenDiffer.instantiateTemplateChildren] 形参注入 +
     * [DataDrivenDiffer.disposeAllDataDrivenChildren] 资源释放），仅循环变量来源不同（来自 forVar1/forVar2）。
     *
     * List/Range 形态：iterable 求值为 List 或任意 Iterable（含 IrRange，drain 成 List）后做 keyed diff，
     * locals = {var1 -> item, var2 -> (double)index}。
     * Map 形态（forOverMap=true）：iterable 求值为 Map，按 entry.key diff，locals = {var1 -> key, var2 -> value}。
     */
    @Suppress("TooGenericExceptionCaught", "NestedBlockDepth")
    private fun buildForContainer(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        // 容器：透明 Box（无视觉）。id 沿用 node 的 "$for_l_c"。标记为透明布局容器
        // （display: contents）：flex 父下布局引擎摊平它、把循环项直接挂到父，使父的
        // gap/align/justify/wrap 真正作用于循环项（见 LayoutNode.transparentLayout）。
        val container: UIElement<*> = IrElementFactory.create("box")
        container.id(node.id)
        container.layoutTransparent(true)

        // for 容器保证 iterableExpr 非 null（isFor 已判定）；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
        val iterableExprNode: ExprNode = ExprBlobReader.readExpr(node.iterableExpr!!)
        // 显式 key 表达式：v28 新增，null 表示使用自动 key 提取（extractListKey）。
        val keyExprNode: ExprNode? = DataDrivenDiffer.parseKeyExprSafely(node)
        val forId: String = node.id

        val effect = Effect {
            // 单次 iterable 求值或 diff 异常不应使容器进入不可恢复状态：捕获后写日志并清空，
            // 下一次依赖变化仍可重建。首次失败打完整堆栈，后续按 id 节流（复用 conditionalFailedIds）。
            try {
                val value: Any? = ExprEvaluator.eval(iterableExprNode, ctx.evalCtx)
                if (node.isForOverMap) {
                    if (value is Map<*, *>) {
                        DataDrivenDiffer.diffForMapChildren(container, node, value, ctx, ::buildElement)
                    } else {
                        if (value != null) {
                            LOGGER.warn("[ui] for 容器 $forId 期望 Map，实际类型 ${value::class.simpleName}，渲染为空")
                        }
                        DataDrivenDiffer.disposeAllDataDrivenChildren(container, ctx)
                        container.replaceChildren(emptyList<UIElement<*>>())
                    }
                } else {
                    val items: List<*>? = coerceForList(value, forId)
                    if (items == null) {
                        DataDrivenDiffer.disposeAllDataDrivenChildren(container, ctx)
                        container.replaceChildren(emptyList<UIElement<*>>())
                    } else {
                        DataDrivenDiffer.diffForListChildren(container, node, items, keyExprNode, ctx, ::buildElement)
                    }
                }
            } catch (t: Throwable) {
                val firstFailureForThisNode = ctx.conditionalFailedIds.add(forId)
                if (firstFailureForThisNode) {
                    LOGGER.warn("[ui] for 容器 $forId iterable 重建失败（首次，详细堆栈）", t)
                } else {
                    LOGGER.warn("[ui] for 容器 $forId iterable 重建再次失败：$t")
                }
                DataDrivenDiffer.disposeAllDataDrivenChildren(container, ctx)
                container.replaceChildren(emptyList<UIElement<*>>())
            }
        }
        container.lifecycleScope().register(effect)
        // 容器自身释放时清理其 dataDrivenChildren 条目，防止嵌套 $for 孤儿泄漏
        DataDrivenDiffer.registerCtxMapCleanup(container, ctx)
        return container
    }

    /**
     * 把 for 的 List 形态可迭代源规整为 List：
     * - [List] 直接返回；
     * - 其他 [Iterable]（含 IrRange）drain 成 ArrayList；
     * - null / 非可迭代 → 返回 null（容器渲染为空）。
     */
    @Suppress("ReturnCount")
    private fun coerceForList(value: Any?, forId: String): List<*>? {
        if (value == null) return null
        if (value is List<*>) return value
        if (value is Iterable<*>) {
            val drained = ArrayList<Any?>()
            for (o in value) {
                drained.add(o)
            }
            return drained
        }
        LOGGER.warn("[ui] for 容器 $forId 的可迭代源不是 List/Iterable（实际类型 ${value::class.simpleName}），渲染为空")
        return null
    }

    // ====== B4：dynamic inventory / slotGrid 运行时展开 ======

    /** 判断节点是否声明 dynamic=true（字面量 Boolean）。 */
    @Suppress("ReturnCount")
    private fun isDynamic(node: UiNode): Boolean {
        val b = node.props["dynamic"]
        if (b == null || b.type != UiBinding.BindingType.LITERAL) return false
        val v = b.literalValue
        return v is Boolean && v
    }

    /** 读字面量 String prop；非 LITERAL 或非 String 视为未提供。 */
    @Suppress("ReturnCount")
    private fun readStringProp(node: UiNode, name: String, def: String): String {
        val b = node.props[name]
        if (b == null || b.type != UiBinding.BindingType.LITERAL) return def
        val v = b.literalValue
        return if (v is String) v else def
    }

    /** 读字面量 int prop；非 LITERAL 或非数值视为未提供。 */
    @Suppress("ReturnCount")
    private fun readIntProp(node: UiNode, name: String, def: Int): Int {
        val b = node.props[name]
        if (b == null || b.type != UiBinding.BindingType.LITERAL) return def
        val v = b.literalValue
        if (v is Number) return v.toInt()
        return def
    }

    /** 读字面量 double prop；非 LITERAL 或非数值视为未提供。 */
    @Suppress("ReturnCount")
    private fun readDoubleProp(node: UiNode, name: String, def: Double): Double {
        val b = node.props[name]
        if (b == null || b.type != UiBinding.BindingType.LITERAL) return def
        val v = b.literalValue
        if (v is Number) return v.toDouble()
        return def
    }

    /** 动态展开 inventory：按 direction 和 slotCount 生成 box(layout=absolute) + N 个 slot。 */
    @Suppress("ReturnCount")
    private fun buildDynamicInventory(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val source = readStringProp(node, "source", "")
        val direction = readStringProp(node, "direction", "horizontal")
        val cellSize = readDoubleProp(node, "cellSize", 18.0)
        val cellPadding = 1.0
        val slotSize = cellSize - 2.0 * cellPadding
        val explicitCount = readIntProp(node, "count", -1)

        val n: Int
        if (explicitCount > 0) {
            n = explicitCount
        } else if (source.isEmpty()) {
            LOGGER.warn("[ui] dynamic inventory ${node.id} 缺少 source 且未给定 count，按空容器处理")
            n = 0
        } else {
            var resolved = ctx.slotCountResolver.resolve(source)
            if (resolved <= 0) {
                LOGGER.warn("[ui] dynamic inventory ${node.id} 的 source=$source 解析为 0 槽，按空容器处理")
                resolved = 0
            }
            n = resolved
        }

        val horizontal = "vertical" != direction
        val width = if (horizontal) n * cellSize else cellSize
        val height = if (horizontal) cellSize else n * cellSize

        val slots: MutableList<UiNode> = ArrayList(n)
        for (i in 0 until n) {
            val x = if (horizontal) i * cellSize else 0.0
            val y = if (horizontal) 0.0 else i * cellSize
            slots.add(buildSlotNode(node, i, x, y, slotSize))
        }

        val boxNode = wrapBoxNode(node, slots, width, height)
        return buildElement(boxNode, ctx)
    }

    /** 动态展开 slotGrid：按 cols 计算行列，生成 N 个 slot；只支持 ROW_MAJOR（dynamic 默认）。 */
    private fun buildDynamicSlotGrid(node: UiNode, ctx: IrInterpreter.Context): UIElement<*> {
        val source = readStringProp(node, "source", "")
        var cols = readIntProp(node, "cols", 1)
        if (cols <= 0) cols = 1
        val cellSize = readDoubleProp(node, "cellSize", 18.0)
        val cellPadding = readDoubleProp(node, "cellPadding", 1.0)
        val slotSize = cellSize - 2.0 * cellPadding
        val indexStart = readIntProp(node, "indexStart", 0)

        val n: Int
        if (source.isEmpty()) {
            LOGGER.warn("[ui] dynamic slotGrid ${node.id} 缺少 source，按空容器处理")
            n = 0
        } else {
            var resolved = ctx.slotCountResolver.resolve(source)
            if (resolved <= 0) {
                LOGGER.warn("[ui] dynamic slotGrid ${node.id} 的 source=$source 解析为 0 槽，按空容器处理")
                resolved = 0
            }
            n = resolved
        }

        val rows = (n + cols - 1) / cols
        val width = cols * cellSize
        val height = rows * cellSize

        val slots: MutableList<UiNode> = ArrayList(n)
        for (i in 0 until n) {
            val col = i % cols
            val row = i / cols
            val x = col * cellSize + cellPadding
            val y = row * cellSize + cellPadding
            slots.add(buildSlotNode(node, indexStart + i, x, y, slotSize))
        }

        val boxNode = wrapBoxNode(node, slots, width, height)
        return buildElement(boxNode, ctx)
    }

    /** 构造单个 slot 的 UiNode：透传共享 prop + 注入 x/y/index/slotSize。 */
    private fun buildSlotNode(parent: UiNode, index: Int, x: Double, y: Double, slotSize: Double): UiNode {
        val props = LinkedHashMap<String, UiBinding>()
        for (name in SHARED_SLOT_PROPS) {
            val b = parent.props[name]
            if (b != null) props[name] = b
        }
        props["x"] = UiBinding.literal(x)
        props["y"] = UiBinding.literal(y)
        props["index"] = UiBinding.literal(index.toLong())
        props["slotSize"] = UiBinding.literal(slotSize)
        return UiNode(
            "slot",
            parent.id + "/slot_" + index,
            props,
            emptyMap<String, String>(),
            emptyList<UiNode>(),
            null,
            null,
            emptyList<String>(),
            null,
            null,
            null,
            null,
            null,
            false,
            null,
            false,
            sharedSlotModifiers(parent),
        )
    }

    private fun sharedSlotModifiers(parent: UiNode): List<UiModifier> {
        return parent.modifiers.mapNotNull { modifier ->
            if (modifier.kind != "style") {
                return@mapNotNull null
            }
            val fields = LinkedHashMap<String, UiBinding>()
            for (e in modifier.fields.entries) {
                if (SHARED_SLOT_MODIFIER_FIELDS.contains(e.key)) {
                    fields[e.key] = e.value
                }
            }
            if (fields.isEmpty()) null else UiModifier(modifier.kind, fields)
        }
    }

    /** 包装 box 容器：保留几何/定位类 prop + layout=absolute + 宽高。 */
    private fun wrapBoxNode(original: UiNode, children: List<UiNode>, width: Double, height: Double): UiNode {
        val props = LinkedHashMap<String, UiBinding>()
        for (name in BOX_KEEP_PROPS) {
            val b = original.props[name]
            if (b != null) props[name] = b
        }
        props["layout"] = UiBinding.literal("absolute")
        props["width"] = UiBinding.literal(width)
        props["height"] = UiBinding.literal(height)
        return UiNode(
            "box",
            original.id,
            props,
            original.animationRefs,
            children,
            null,
            null,
            emptyList<String>(),
            null,
            null,
            null,
            null,
            null,
            false,
            null,
            original.isAnimateLayout,
            original.modifiers,
        )
    }
}
